// ProductsAdmin.js
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
} from "@mui/material";

import theme from "./theme";
import NavBar from "./components/NavBar";
import { isAdmin, getToken } from "./auth/token";
import BACKEND_URL from "./config";

function authHeaders() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function ProductsAdmin() {
  const navigate = useNavigate();
  const admin = isAdmin();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [products, setProducts] = useState([]);
  const [industries, setIndustries] = useState([]);

  const [createForm, setCreateForm] = useState({
    name: "",
    slug: "",
    description: "",
    industry_ids: [],
    is_active: true,
  });

  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "",
    slug: "",
    description: "",
    industry_ids: [],
    is_active: true,
  });

  // Redirect non-admins
  useEffect(() => {
    if (!admin) {
      navigate("/");
    }
  }, [admin, navigate]);

  async function fetchIndustries() {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/industries`, {
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setIndustries(data);
    } catch (err) {
      console.error("Error fetching industries", err);
      setError("Failed to load industries list.");
    }
  }

  async function fetchProducts() {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/products`, {
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setProducts(data);
    } catch (err) {
      console.error("Error fetching products", err);
      setError("Failed to load products.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (admin) {
      fetchIndustries();
      fetchProducts();
    }
  }, [admin]);

  const sortedProducts = useMemo(() => {
    return [...products].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [products]);

  function handleCreateFormChange(e) {
    const { name, value, type, checked, options } = e.target;

    if (name === "industry_ids") {
      const selected = Array.from(options)
        .filter((opt) => opt.selected)
        .map((opt) => opt.value);
      setCreateForm((prev) => ({
        ...prev,
        industry_ids: selected,
      }));
      return;
    }

    setCreateForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  }

  async function handleCreate(e) {
    e.preventDefault();
    setError("");

    try {
      const payload = {
        name: createForm.name.trim(),
        slug: createForm.slug.trim() || undefined,
        description: createForm.description.trim(),
        industry_ids: createForm.industry_ids || [],
        is_active: !!createForm.is_active,
      };

      const res = await fetch(`${BACKEND_URL}/api/admin/products`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
        body: JSON.stringify(payload),
      });

      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.message || body.error || `HTTP ${res.status}`);
      }

      // Clear form
      setCreateForm({
        name: "",
        slug: "",
        description: "",
        industry_ids: [],
        is_active: true,
      });

      fetchProducts();
    } catch (err) {
      console.error("Error creating product", err);
      setError(err.message || "Failed to create product.");
    }
  }

  function startEditing(prod) {
    setEditingId(prod.id);
    setEditForm({
      name: prod.name || "",
      slug: prod.slug || "",
      description: prod.description || "",
      industry_ids: prod.industry_ids || [],
      is_active: prod.is_active !== false,
    });
  }

  function handleEditFormChange(e) {
    const { name, value, type, checked, options } = e.target;

    if (name === "industry_ids") {
      const selected = Array.from(options)
        .filter((opt) => opt.selected)
        .map((opt) => opt.value);
      setEditForm((prev) => ({
        ...prev,
        industry_ids: selected,
      }));
      return;
    }

    setEditForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  }

  async function handleSaveEdit(e) {
    e.preventDefault();
    if (!editingId) return;
    setError("");

    try {
      const payload = {
        name: editForm.name.trim(),
        slug: editForm.slug.trim() || undefined,
        description: editForm.description.trim(),
        industry_ids: editForm.industry_ids || [],
        is_active: !!editForm.is_active,
      };

      const res = await fetch(`${BACKEND_URL}/api/admin/products/${editingId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
        body: JSON.stringify(payload),
      });

      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.message || body.error || `HTTP ${res.status}`);
      }

      setEditingId(null);
      fetchProducts();
    } catch (err) {
      console.error("Error updating product", err);
      setError(err.message || "Failed to update product.");
    }
  }

  function labelIndustries(ids) {
    if (!ids || ids.length === 0) return "";
    const map = new Map(industries.map((ind) => [ind.id, ind]));
    return ids
      .map((id) => {
        const ind = map.get(id);
        return ind ? `${ind.name} (${ind.slug})` : id;
      })
      .join(", ");
  }

  return (
    <ThemeProvider theme={theme}>
      <NavBar />
      <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
        <Typography variant="h4" sx={{ mb: 3 }}>
          Products admin
        </Typography>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* Create new product */}
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Create new product
              </Typography>

              <form onSubmit={handleCreate}>
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  }}
                >
                  <label>
                    Name*
                    <input
                      type="text"
                      name="name"
                      value={createForm.name}
                      onChange={handleCreateFormChange}
                      style={{ width: "100%", marginTop: 4 }}
                    />
                  </label>

                  <label>
                    Slug (optional)
                    <input
                      type="text"
                      name="slug"
                      value={createForm.slug}
                      onChange={handleCreateFormChange}
                      style={{ width: "100%", marginTop: 4 }}
                    />
                  </label>

                  <label style={{ gridColumn: "1 / -1" }}>
                    Description
                    <textarea
                      name="description"
                      value={createForm.description}
                      onChange={handleCreateFormChange}
                      rows={3}
                      style={{ width: "100%", marginTop: 4 }}
                    />
                  </label>

                  <label>
                    Industries
                    <select
                      multiple
                      name="industry_ids"
                      value={createForm.industry_ids}
                      onChange={handleCreateFormChange}
                      style={{ width: "100%", marginTop: 4, minHeight: 100 }}
                    >
                      {industries.map((ind) => (
                        <option key={ind.id} value={ind.id}>
                          {ind.name} ({ind.slug})
                        </option>
                      ))}
                    </select>
                    <div style={{ fontSize: 12, marginTop: 4 }}>
                      Hold Ctrl (Windows) or Command (Mac) to select multiple.
                    </div>
                  </label>

                  <label>
                    Active?
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={!!createForm.is_active}
                      onChange={handleCreateFormChange}
                      style={{ marginLeft: 8 }}
                    />
                  </label>
                </Box>

                <Box sx={{ mt: 2 }}>
                  <Button type="submit" variant="contained">
                    Create product
                  </Button>
                </Box>
              </form>
            </Paper>

            {/* List / edit products */}
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Existing products
              </Typography>

              {sortedProducts.length === 0 ? (
                <Typography>No products found.</Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Slug</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Industries</TableCell>
                        <TableCell>Active?</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell>Updated</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedProducts.map((prod) => (
                        <TableRow key={prod.id}>
                          <TableCell>{prod.name}</TableCell>
                          <TableCell>{prod.slug}</TableCell>
                          <TableCell sx={{ maxWidth: 220 }}>
                            <span style={{ fontSize: "0.9rem" }}>
                              {prod.description}
                            </span>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 260, fontSize: "0.8rem" }}>
                            {labelIndustries(prod.industry_ids)}
                          </TableCell>
                          <TableCell>{prod.is_active ? "Yes" : "No"}</TableCell>
                          <TableCell sx={{ fontSize: "0.75rem" }}>
                            {prod.created_at}
                          </TableCell>
                          <TableCell sx={{ fontSize: "0.75rem" }}>
                            {prod.updated_at}
                          </TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => startEditing(prod)}
                            >
                              Edit
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>

            {/* Edit panel */}
            {editingId && (
              <Paper sx={{ p: 2, mt: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Edit product
                </Typography>

                <form onSubmit={handleSaveEdit}>
                  <Box
                    sx={{
                      display: "grid",
                      gap: 2,
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    }}
                  >
                    <label>
                      Name
                      <input
                        type="text"
                        name="name"
                        value={editForm.name}
                        onChange={handleEditFormChange}
                        style={{ width: "100%", marginTop: 4 }}
                      />
                    </label>

                    <label>
                      Slug
                      <input
                        type="text"
                        name="slug"
                        value={editForm.slug}
                        onChange={handleEditFormChange}
                        style={{ width: "100%", marginTop: 4 }}
                      />
                    </label>

                    <label style={{ gridColumn: "1 / -1" }}>
                      Description
                      <textarea
                        name="description"
                        value={editForm.description}
                        onChange={handleEditFormChange}
                        rows={3}
                        style={{ width: "100%", marginTop: 4 }}
                      />
                    </label>

                    <label>
                      Industries
                      <select
                        multiple
                        name="industry_ids"
                        value={editForm.industry_ids}
                        onChange={handleEditFormChange}
                        style={{ width: "100%", marginTop: 4, minHeight: 100 }}
                      >
                        {industries.map((ind) => (
                          <option key={ind.id} value={ind.id}>
                            {ind.name} ({ind.slug})
                          </option>
                        ))}
                      </select>
                      <div style={{ fontSize: 12, marginTop: 4 }}>
                        Hold Ctrl (Windows) or Command (Mac) to select multiple.
                      </div>
                    </label>

                    <label>
                      Active?
                      <input
                        type="checkbox"
                        name="is_active"
                        checked={!!editForm.is_active}
                        onChange={handleEditFormChange}
                        style={{ marginLeft: 8 }}
                      />
                    </label>
                  </Box>

                  <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
                    <Button type="submit" variant="contained">
                      Save
                    </Button>
                    <Button
                      type="button"
                      variant="text"
                      onClick={() => setEditingId(null)}
                    >
                      Cancel
                    </Button>
                  </Box>
                </form>
              </Paper>
            )}
          </>
        )}
      </Box>
    </ThemeProvider>
  );
}

export default ProductsAdmin;
